import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  type BuilderPhoneDialOption,
  dialFlagCountry,
  flagCdnSrc,
} from '../../utils/builderPhoneDial'
import BuilderStepHead from './BuilderStepHead'
import BuilderCityPicker from './BuilderCityPicker'

const TITLE_MAX = 100

type Props = {
  title: string
  onTitleChange: (value: string) => void
  titleSuggestions: string[]
  onApplyTitleSuggestion: (value: string) => void
  sellerCity: string
  onSellerCityChange: (value: string) => void
  userCities: string[]
  catalogCityOptions: string[]
  onOpenAccountSettings?: () => void
  needsContact: boolean
  isLoggedIn: boolean
  sellerFullName: string
  onSellerFullNameChange: (value: string) => void
  sellerEmail: string
  onSellerEmailChange: (value: string) => void
  sellerPhone: string
  onSellerPhoneChange: (value: string) => void
  showFullNameField: boolean
  phoneDialCode: string
  phoneDialOptions: readonly BuilderPhoneDialOption[]
  onPhoneDialCodeChange: (value: string) => void
  guestSignInPanel?: ReactNode
}

function IconMapPin() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path d="M12 21s7-4.5 7-11a7 7 0 1 0-14 0c0 6.5 7 11 7 11z" strokeLinejoin="round" />
      <circle cx="12" cy="10" r="2.5" />
    </svg>
  )
}

function IconMail() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 7l9 6 9-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function IconPhone() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <path
        d="M6.5 4h3l1.5 4-2 1.5a11 11 0 0 0 5 5L17.5 13 21 14.5V18a2 2 0 0 1-2 2C9.6 20 4 14.4 4 6.5A2 2 0 0 1 6.5 4z"
        strokeLinejoin="round"
      />
    </svg>
  )
}

function IconUser() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c1.5-3 4-4.5 7-4.5s5.5 1.5 7 4.5" strokeLinecap="round" />
    </svg>
  )
}

function inflowRowClass(...parts: Array<string | false | undefined>) {
  return ['builder-inflow-row', ...parts.filter(Boolean)].join(' ')
}

type InflowRowProps = {
  prefix?: ReactNode
  trailing?: ReactNode
  rowClassName?: string
  slotClassName?: string
  children: ReactNode
}

function BuilderInflowRow({ prefix, trailing, rowClassName, slotClassName, children }: InflowRowProps) {
  return (
    <div className={inflowRowClass(rowClassName)}>
      {prefix ? (
        <span className="builder-inflow-row__prefix" aria-hidden>
          {prefix}
        </span>
      ) : null}
      <div className={['builder-inflow-row__slot', slotClassName].filter(Boolean).join(' ')}>{children}</div>
      {trailing ? (
        <span className="builder-inflow-row__trail" aria-hidden>
          {trailing}
        </span>
      ) : null}
    </div>
  )
}

export default function BuilderContactStep({
  title,
  onTitleChange,
  titleSuggestions,
  onApplyTitleSuggestion,
  sellerCity,
  onSellerCityChange,
  userCities,
  catalogCityOptions,
  onOpenAccountSettings,
  needsContact,
  isLoggedIn,
  sellerFullName,
  onSellerFullNameChange,
  sellerEmail,
  onSellerEmailChange,
  sellerPhone,
  onSellerPhoneChange,
  showFullNameField,
  phoneDialCode,
  phoneDialOptions,
  onPhoneDialCodeChange,
  guestSignInPanel,
}: Props) {
  const { t } = useTranslation()
  const titleLen = title.length
  const profileCities = isLoggedIn && userCities.length > 0 ? userCities : []
  const pickableCities = profileCities.length > 0 ? profileCities : catalogCityOptions
  const locationLabel = sellerCity.trim() || pickableCities[0]?.trim() || ''
  const selectedDialCountry = dialFlagCountry(phoneDialCode)

  return (
    <div className="builder-contact-step builder-step-surface builder-step-surface--flat">
      <BuilderStepHead title={t('listing.builder.stepContact')} lead={t('listing.builder.contactStepLead')} />

      <section className="builder-contact-section" aria-labelledby="builder-contact-title-heading">
        <h3 id="builder-contact-title-heading" className="builder-contact-section__title">
          {t('listing.builder.contactTitleSection')}
        </h3>
        <BuilderInflowRow rowClassName="builder-inflow-row--compact">
          <input
            type="text"
            className="builder-inflow-row__input builder-inflow-row__input--with-meta"
            value={title}
            maxLength={TITLE_MAX}
            onChange={(e) => onTitleChange(e.target.value)}
            aria-describedby="builder-contact-title-count"
          />
          <span id="builder-contact-title-count" className="builder-inflow-row__meta">
            {titleLen}/{TITLE_MAX}
          </span>
        </BuilderInflowRow>
        {titleSuggestions.length > 0 ? (
          <div className="builder-contact-title-suggestions">
            <span className="builder-contact-title-suggestions__label">{t('listing.builder.contactSuggestionsLabel')}</span>
            {titleSuggestions.map((s) => (
              <button
                key={s}
                type="button"
                className="builder-contact-title-chip"
                onClick={() => onApplyTitleSuggestion(s)}
              >
                <span>{s}</span>
                <span aria-hidden>+</span>
              </button>
            ))}
          </div>
        ) : null}
      </section>

      <hr className="builder-contact-divider" />

      <section className="builder-contact-section" aria-labelledby="builder-contact-location-heading">
        <h3 id="builder-contact-location-heading" className="builder-contact-section__title">
          {t('listing.builder.contactLocationShort')}
        </h3>
        {pickableCities.length > 0 ? (
          <BuilderCityPicker
            cities={pickableCities}
            value={sellerCity}
            onChange={onSellerCityChange}
            placeholder={t('listing.builder.contactLocationAddCta')}
            mapPinIcon={<IconMapPin />}
          />
        ) : (
          <BuilderInflowRow prefix={<IconMapPin />}>
            <input
              type="text"
              className="builder-inflow-row__input"
              value={sellerCity}
              onChange={(e) => onSellerCityChange(e.target.value)}
              placeholder={t('listing.builder.contactLocationAddCta')}
              aria-label={t('listing.sellerCityLabel')}
              autoComplete="address-level2"
              maxLength={80}
            />
          </BuilderInflowRow>
        )}
        {profileCities.length > 0 && locationLabel ? (
          <button type="button" className="builder-contact-link" onClick={() => onOpenAccountSettings?.()}>
            {t('listing.builder.contactAccountSettings')}
          </button>
        ) : null}
      </section>

      {needsContact ? (
        <>
          <hr className="builder-contact-divider" />
          <section className="builder-contact-section" aria-labelledby="builder-contact-details-heading">
            <h3 id="builder-contact-details-heading" className="builder-contact-section__title">
              {t('listing.builder.contactSectionShort')}
            </h3>
            <p className="builder-contact-section__hint">{t('listing.builder.contactMethodRequired')}</p>

            <div className="builder-contact-fields">
              {showFullNameField ? (
                <BuilderInflowRow prefix={<IconUser />}>
                  <input
                    type="text"
                    className="builder-inflow-row__input"
                    value={sellerFullName}
                    onChange={(e) => onSellerFullNameChange(e.target.value)}
                    autoComplete="name"
                    placeholder={t('sellerVerify.fullName')}
                    aria-label={t('sellerVerify.fullName')}
                  />
                </BuilderInflowRow>
              ) : null}

              {!isLoggedIn ? (
                <BuilderInflowRow prefix={<IconMail />}>
                  <input
                    type="email"
                    className="builder-inflow-row__input"
                    value={sellerEmail}
                    onChange={(e) => onSellerEmailChange(e.target.value)}
                    autoComplete="email"
                    placeholder={t('listing.builder.contactEmailPlaceholder')}
                    aria-label={t('listing.builder.contactEmailPlaceholder')}
                  />
                </BuilderInflowRow>
              ) : null}

              <BuilderInflowRow prefix={<IconPhone />}>
                <div className="builder-inflow-row__dial">
                  {selectedDialCountry ? (
                    <img
                      className="builder-inflow-row__dial-flag"
                      src={flagCdnSrc(selectedDialCountry, 20)}
                      srcSet={`${flagCdnSrc(selectedDialCountry, 40)} 2x`}
                      alt=""
                      width={20}
                      height={15}
                      loading="lazy"
                      decoding="async"
                    />
                  ) : (
                    <span className="builder-inflow-row__dial-globe" aria-hidden>
                      🌐
                    </span>
                  )}
                  <select
                    className="builder-inflow-row__dial-select"
                    value={phoneDialCode}
                    onChange={(e) => onPhoneDialCodeChange(e.target.value)}
                    aria-label={t('listing.builder.contactPhoneDial')}
                  >
                    {phoneDialOptions.map((option) => (
                      <option key={option.dial} value={option.dial}>
                        {option.dial}
                      </option>
                    ))}
                  </select>
                  <span className="builder-inflow-row__dial-chevron" aria-hidden>
                    ▼
                  </span>
                </div>
                <input
                  type="tel"
                  className="builder-inflow-row__input builder-inflow-row__input--after-dial"
                  value={sellerPhone}
                  onChange={(e) => onSellerPhoneChange(e.target.value)}
                  autoComplete="tel"
                  placeholder={t('listing.builder.contactPhoneField')}
                  aria-label={t('listing.builder.contactPhoneField')}
                />
              </BuilderInflowRow>
            </div>

            {guestSignInPanel}
          </section>
        </>
      ) : null}
    </div>
  )
}
